// Questions: the list, the editor, and the import of a boss report.
import { useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faMagnifyingGlass, faFloppyDisk, faTrash, faPlay, faFileImport, faXmark, faCheck, faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import CheckEditor, { cleanChecks } from './CheckEditor';
import { ASSISTANT, CHECK_LABEL, SCOPE, VARIANT, VARIANTS_OF, blankCase } from './format';

function Toggles({ options, value, onChange, labels }) {
  const set = new Set(value || []);
  return (
    <div className="ts-toggles">
      {options.map((o) => (
        <button key={o} type="button" className={set.has(o) ? 'on' : ''}
          onClick={() => { const n = new Set(set); if (n.has(o)) n.delete(o); else n.add(o); onChange([...n]); }}>
          {labels?.[o] || o}
        </button>
      ))}
      <span className="ts-toggles-note">{set.size ? '' : 'alle'}</span>
    </div>
  );
}

export function CaseForm({ value, onChange, data, compact }) {
  const c = value;
  const set = (patch) => onChange({ ...c, ...patch });
  const dataSets = (data.data_sets || []).filter((d) => (c.assistant === 'ppi' ? d.model === 'ppi' : d.model === 'efd'));
  return (
    <div className="ts-form">
      <label className="ts-field">
        <span>Frage — so, wie ein Nutzer sie stellt</span>
        <textarea rows={compact ? 2 : 3} value={c.question} onChange={(e) => set({ question: e.target.value })} />
      </label>
      <div className="ts-row wrap">
        <label className="ts-field">
          <span>Assistent</span>
          <select value={c.assistant} onChange={(e) => set({ assistant: e.target.value, variants: [], data_sets: [] })}>
            <option value="efd">Bro EFD</option>
            <option value="ppi">Bro PPI</option>
          </select>
        </label>
        <label className="ts-field">
          <span>Sprache</span>
          <select value={c.language || 'de'} onChange={(e) => set({ language: e.target.value })}>
            {['de', 'en', 'fr', 'it'].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <label className="ts-field grow">
          <span>Herkunft</span>
          <input value={c.origin || ''} placeholder="z. B. Chef 21.10.2026, F1" onChange={(e) => set({ origin: e.target.value })} />
        </label>
        <label className="ts-check-inline">
          <input type="checkbox" checked={c.active !== false} onChange={(e) => set({ active: e.target.checked })} /> aktiv
        </label>
      </div>
      <div className="ts-scope">
        <div><span className="ts-mini">Varianten</span>
          <Toggles options={VARIANTS_OF[c.assistant] || []} value={c.variants} labels={VARIANT} onChange={(v) => set({ variants: v })} /></div>
        <div><span className="ts-mini">Scope-Typen</span>
          <Toggles options={c.assistant === 'ppi' ? ['all', 'consolidation', 'portfolio'] : ['all', 'advisor']}
            value={c.scope_types} labels={SCOPE} onChange={(v) => set({ scope_types: v })} /></div>
        <div><span className="ts-mini">Datensätze</span>
          <Toggles options={dataSets.map((d) => d.key)} value={c.data_sets}
            labels={Object.fromEntries(dataSets.map((d) => [d.key, d.name]))} onChange={(v) => set({ data_sets: v })} /></div>
      </div>
      <div className="ts-mini">Woran eine richtige Antwort erkennbar ist</div>
      <CheckEditor checks={c.checks || []} metrics={data.metrics || []} onChange={(checks) => set({ checks })} />
      {!compact && (
        <label className="ts-field">
          <span>Notiz</span>
          <input value={c.notes || ''} onChange={(e) => set({ notes: e.target.value })} />
        </label>
      )}
    </div>
  );
}

const payload = (c) => ({
  assistant: c.assistant, variants: c.variants || [], scope_types: c.scope_types || [], data_sets: c.data_sets || [],
  question: c.question, language: c.language || 'de', checks: cleanChecks(c.checks), origin: c.origin || '',
  notes: c.notes || '', active: c.active !== false,
});

function ImportPanel({ client, data, onDone, flash }) {
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [origin, setOrigin] = useState('');
  const [assistant, setAssistant] = useState('efd');
  const [busy, setBusy] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [keep, setKeep] = useState({});
  const inputRef = useRef(null);

  const analyse = async () => {
    setBusy(true);
    try {
      let result;
      if (file) {
        const form = new FormData();
        form.append('file', file);
        form.append('assistant', assistant);
        if (origin) form.append('origin', origin);
        const res = await fetch(`${client.target.base}/api/admin/tests/import`, {
          method: 'POST', headers: client.target.token ? { Authorization: `Bearer ${client.target.token}` } : {}, body: form,
        });
        result = await res.json();
        if (!res.ok) throw new Error(result.error || `HTTP ${res.status}`);
      } else {
        result = await client.post('/api/admin/tests/import', { text, assistant, origin });
      }
      setProposals(result.cases || []);
      setKeep(Object.fromEntries((result.cases || []).map((_, i) => [i, true])));
      flash(`${(result.cases || []).length} Vorschläge aus ${result.images || 0} Bildern — bitte prüfen.`);
    } catch (e) {
      flash(e.message, true);
    }
    setBusy(false);
  };

  const save = async () => {
    const chosen = proposals.filter((_, i) => keep[i]);
    setBusy(true);
    try {
      await client.post('/api/admin/tests/cases/bulk', { cases: chosen.map(payload) });
      flash(`${chosen.length} Fragen gespeichert.`);
      setProposals(null);
      setFile(null);
      setText('');
      onDone();
    } catch (e) {
      flash(e.message, true);
    }
    setBusy(false);
  };

  if (proposals) {
    return (
      <div className="ts-import">
        <div className="ts-import-head">
          <h3>Vorschläge prüfen</h3>
          <p>Die erwarteten Werte stammen aus den Dashboard-Screenshots. Prüfe jeden Vorschlag, korrigiere, wo nötig, und speichere die richtigen.</p>
        </div>
        {proposals.map((p, i) => (
          <div key={i} className={`ts-proposal ${keep[i] ? '' : 'off'}`}>
            <label className="ts-check-inline strong">
              <input type="checkbox" checked={!!keep[i]} onChange={(e) => setKeep({ ...keep, [i]: e.target.checked })} />
              Übernehmen {p.notes ? <em>— {p.notes}</em> : null}
            </label>
            <CaseForm value={p} data={data} compact
              onChange={(v) => setProposals(proposals.map((x, j) => (j === i ? v : x)))} />
          </div>
        ))}
        <div className="ts-actions">
          <button type="button" className="ts-btn" disabled={busy} onClick={save}>
            <FontAwesomeIcon icon={faCheck} /> {Object.values(keep).filter(Boolean).length} Fragen speichern
          </button>
          <button type="button" className="ts-btn ghost" onClick={() => setProposals(null)}>Verwerfen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ts-import">
      <div className="ts-import-head">
        <h3><FontAwesomeIcon icon={faFileImport} /> Bericht importieren</h3>
        <p>Word-Datei mit Screenshots (Chat links, Dashboard rechts) oder einfach Text einfügen. Die KI schlägt daraus Fragen mit den Werten des Dashboards vor — gespeichert wird erst nach deiner Prüfung.</p>
      </div>
      <div className={`ts-drop ${file ? 'has' : ''}`} onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}>
        <input ref={inputRef} type="file" accept=".docx" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {file ? <><strong>{file.name}</strong><span>{Math.round(file.size / 1024)} KB</span></>
          : <><strong>.docx hierher ziehen</strong><span>oder klicken zum Auswählen</span></>}
      </div>
      {!file && (
        <label className="ts-field">
          <span>…oder Text einfügen</span>
          <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Frage: … / Dashboard zeigt: …" />
        </label>
      )}
      <div className="ts-row">
        <label className="ts-field">
          <span>Assistent (falls unklar)</span>
          <select value={assistant} onChange={(e) => setAssistant(e.target.value)}>
            <option value="efd">Bro EFD</option>
            <option value="ppi">Bro PPI</option>
          </select>
        </label>
        <label className="ts-field grow">
          <span>Herkunft</span>
          <input value={origin} placeholder="z. B. Chef 21.10.2026" onChange={(e) => setOrigin(e.target.value)} />
        </label>
      </div>
      <div className="ts-actions">
        <button type="button" className="ts-btn" disabled={busy || (!file && !text.trim())} onClick={analyse}>
          <FontAwesomeIcon icon={faWandMagicSparkles} /> {busy ? 'Liest den Bericht …' : 'Vorschläge erstellen'}
        </button>
        {file && <button type="button" className="ts-btn ghost" onClick={() => setFile(null)}><FontAwesomeIcon icon={faXmark} /> Datei entfernen</button>}
      </div>
    </div>
  );
}

export default function CasesView({ client, data, reload, flash, onRun, lastStatus }) {
  const [q, setQ] = useState('');
  const [assistant, setAssistant] = useState('');
  const [editing, setEditing] = useState(null);     // {case} | {case, isNew}
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);

  const cases = useMemo(() => (data.cases || []).filter((c) => (!assistant || c.assistant === assistant)
    && (!q || `${c.question} ${c.origin} ${c.notes}`.toLowerCase().includes(q.toLowerCase()))), [data.cases, q, assistant]);

  const save = async () => {
    setBusy(true);
    try {
      const body = payload(editing.case);
      if (editing.isNew) await client.post('/api/admin/tests/cases', body);
      else await client.put(`/api/admin/tests/cases/${editing.case.id}`, body);
      flash('Frage gespeichert.');
      setEditing(null);
      reload();
    } catch (e) {
      flash(e.message, true);
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('Diese Frage löschen? Frühere Ergebnisse bleiben erhalten.')) return;
    try {
      await client.del(`/api/admin/tests/cases/${editing.case.id}`);
      flash('Frage gelöscht.');
      setEditing(null);
      reload();
    } catch (e) {
      flash(e.message, true);
    }
  };

  if (importing) {
    return (
      <div className="ts-pane">
        <button type="button" className="ts-back" onClick={() => setImporting(false)}>← Zurück zu den Fragen</button>
        <ImportPanel client={client} data={data} flash={flash} onDone={() => { setImporting(false); reload(); }} />
      </div>
    );
  }

  if (editing) {
    return (
      <div className="ts-pane">
        <button type="button" className="ts-back" onClick={() => setEditing(null)}>← Zurück zu den Fragen</button>
        <h2 className="ts-h2">{editing.isNew ? 'Neue Frage' : `Frage #${editing.case.id}`}</h2>
        <CaseForm value={editing.case} data={data} onChange={(c) => setEditing({ ...editing, case: c })} />
        <div className="ts-actions sticky">
          <button type="button" className="ts-btn" disabled={busy} onClick={save}><FontAwesomeIcon icon={faFloppyDisk} /> Speichern</button>
          {!editing.isNew && (
            <button type="button" className="ts-btn ghost" onClick={() => onRun({ case_ids: [editing.case.id] })}>
              <FontAwesomeIcon icon={faPlay} /> Nur diese testen
            </button>
          )}
          {!editing.isNew && <button type="button" className="ts-btn ghost danger" onClick={remove}><FontAwesomeIcon icon={faTrash} /> Löschen</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="ts-pane">
      <div className="ts-toolbar">
        <div className="ts-search"><FontAwesomeIcon icon={faMagnifyingGlass} />
          <input value={q} placeholder="Fragen durchsuchen" onChange={(e) => setQ(e.target.value)} /></div>
        <select value={assistant} onChange={(e) => setAssistant(e.target.value)}>
          <option value="">Alle Assistenten</option><option value="efd">Bro EFD</option><option value="ppi">Bro PPI</option>
        </select>
        <span className="grow" />
        <button type="button" className="ts-btn ghost" onClick={() => setImporting(true)}><FontAwesomeIcon icon={faFileImport} /> Bericht importieren</button>
        <button type="button" className="ts-btn" onClick={() => setEditing({ case: blankCase(assistant || 'efd'), isNew: true })}>
          <FontAwesomeIcon icon={faPlus} /> Neue Frage
        </button>
      </div>
      <div className="ts-cases">
        {cases.map((c) => (
          <button key={c.id} type="button" className={`ts-case ${c.active ? '' : 'off'}`} onClick={() => setEditing({ case: c })}>
            <span className={`ts-dot s-${lastStatus(c.id)}`} />
            <span className="ts-case-main">
              <span className="ts-case-q">{c.question}</span>
              <span className="ts-case-meta">
                <span className="ts-tag">{ASSISTANT[c.assistant]}</span>
                {(c.variants || []).map((v) => <span key={v} className="ts-tag soft">{VARIANT[v] || v}</span>)}
                {(c.scope_types || []).map((s) => <span key={s} className="ts-tag soft">{SCOPE[s] || s}</span>)}
                <span className="ts-muted">{(c.checks || []).length} Prüfungen · {[...new Set((c.checks || []).map((k) => CHECK_LABEL[k.type]))].join(', ')}</span>
                {c.origin && <span className="ts-muted">· {c.origin}</span>}
              </span>
            </span>
          </button>
        ))}
        {!cases.length && <div className="ts-empty">Keine Fragen gefunden.</div>}
      </div>
    </div>
  );
}
